/** Typed client for the Worker API. Speaks only in shared types. */

import type {
  Client,
  ClientInput,
  DateRange,
  Entry,
  EntryInput,
  Me,
  PublishRequest,
  Report,
} from '$shared/types';

export class ApiFailure extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** True when the Google grant is gone and the user must sign in again. */
    readonly reauth = false,
  ) {
    super(message);
    this.name = 'ApiFailure';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init.headers },
    // The session cookie is HttpOnly and same-origin; this keeps it attached.
    credentials: 'same-origin',
  });

  if (res.status === 204) return undefined as T;

  const body = (await res
    .json()
    .catch(() => ({ error: res.statusText }))) as { error?: string; reauth?: boolean } & T;

  if (!res.ok) {
    throw new ApiFailure(body.error ?? 'Request failed', res.status, Boolean(body.reauth));
  }
  return body as T;
}

export const api = {
  me: () => request<Me>('/me'),

  listClients: () => request<Client[]>('/clients'),
  createClient: (input: ClientInput) =>
    request<Client>('/clients', { method: 'POST', body: JSON.stringify(input) }),
  updateClient: (id: string, patch: Partial<ClientInput>) =>
    request<Client>(`/clients/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  rebuildClients: () => request<Client[]>('/clients/rebuild', { method: 'POST' }),

  listEntries: (clientId: string, range?: DateRange) => {
    const qs = new URLSearchParams({ clientId });
    if (range) {
      qs.set('from', range.from);
      qs.set('to', range.to);
    }
    return request<Entry[]>(`/entries?${qs}`);
  },
  createEntry: (input: EntryInput) =>
    request<Entry>('/entries', { method: 'POST', body: JSON.stringify(input) }),
  updateEntry: (id: string, patch: Partial<EntryInput>) =>
    request<Entry>(`/entries/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteEntry: (clientId: string, id: string) =>
    request<void>(`/entries/${id}?clientId=${encodeURIComponent(clientId)}`, { method: 'DELETE' }),

  importEntries: (clientId: string, entries: EntryInput[]) =>
    request<{ imported: number }>('/import', {
      method: 'POST',
      body: JSON.stringify({ clientId, entries }),
    }),

  listPublished: () =>
    request<
      {
        id: string;
        clientName: string;
        from: string;
        to: string;
        hours: number;
        publishedAt: string;
        expiresAt?: string;
      }[]
    >('/reports'),
  publishReport: (report: Report, expiresInDays?: number) =>
    request<{ id: string; url: string }>('/reports/publish', {
      method: 'POST',
      body: JSON.stringify({ report, expiresInDays } satisfies PublishRequest),
    }),
  revokeReport: (id: string) => request<void>(`/reports/${id}`, { method: 'DELETE' }),

  // Not under /api, so it bypasses the request helper.
  logout: async (): Promise<void> => {
    await fetch('/auth/logout', { method: 'POST', credentials: 'same-origin' });
  },
};
